import { BuyCreditsModal } from "@/components/credits/BuyCreditsModal";
import { MainWindow, PageContent } from "@/components/layout/MainWindow";
import { PageHeader } from "@/components/layout/PageHeader";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import z from "zod";

const unsafeNavigateOptionsSchema = z.object({
  to: z.string(),
  search: z.record(z.string(), z.any()).optional(),
  state: z.record(z.string(), z.any()).optional(),
});

const historyStateSchema = z
  .object({
    reason: z.enum(["notEnoughCredits", "riskOfRunningOut"]).optional(),
    creditsNeededRestOfPeriod: z.number().optional(),
    creditEstimateRestOfPeriod: z.number().optional(),
    neededCredits: z.number().optional(),
    neededPrepaidCredits: z.number().optional(),
    allowPrepaidCredits: z.boolean().optional(),
    closeNavigateOptions: unsafeNavigateOptionsSchema,
    successNavigateOptions: unsafeNavigateOptionsSchema,
    skipNavigateOptions: unsafeNavigateOptionsSchema.optional(),
  })
  .optional()
  .catch(undefined);

const parseHistoryState = (historyState: z.infer<typeof historyStateSchema>) =>
  historyStateSchema.parse(historyState);

export const Route = createFileRoute("/_dashboard/buy-credits")({
  staticData: {
    crumb: "Tilkøb credits",
    meta: {
      title: "Tilkøb credits",
    },
  },
  beforeLoad: ({ location }) => {
    const historyState = parseHistoryState(
      location.state.routeStates?.[Route.id],
    );

    if (!historyState) {
      throw redirect({
        to: "/",
        replace: true,
      });
    }

    return {
      parseHistoryState,
      useHistoryState: () => historyState,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { useHistoryState } = Route.useRouteContext();
  const navigate = useNavigate();
  const {
    reason,
    creditsNeededRestOfPeriod,
    creditEstimateRestOfPeriod,
    neededCredits,
    neededPrepaidCredits,
    allowPrepaidCredits,
    closeNavigateOptions,
    successNavigateOptions,
    skipNavigateOptions,
  } = useHistoryState();

  return (
    <MainWindow>
      <PageHeader title="Tilkøb credits" />
      <div className="flex w-full justify-center">
        <PageContent className="flex size-full h-fit flex-col px-14 pt-7">
          <BuyCreditsModal
            reason={reason}
            creditsNeededRestOfPeriod={creditsNeededRestOfPeriod}
            creditEstimateRestOfPeriod={creditEstimateRestOfPeriod}
            neededCredits={neededCredits}
            neededPrepaidCredits={neededPrepaidCredits}
            allowPrepaidCredits={allowPrepaidCredits}
            onClose={async () => await navigate(closeNavigateOptions)}
            onSuccess={async () => await navigate(successNavigateOptions)}
            onSkip={
              skipNavigateOptions
                ? async () => await navigate(skipNavigateOptions)
                : undefined
            }
          />
        </PageContent>
      </div>
    </MainWindow>
  );
}
